import React, { useContext, useEffect, useReducer, useState } from "react";

import {
  Button,
  IconButton,
  makeStyles,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  Card,
  Box,
  CircularProgress,
} from "@material-ui/core";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import Title from "../../components/Title";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import api from "../../services/api";
import { DeleteOutline, Edit, Queue as QueueIcon } from "@material-ui/icons";
import QueueModal from "../../components/QueueModal";
import { toast } from "react-toastify";
import ConfirmationModal from "../../components/ConfirmationModal";
import { SocketContext } from "../../context/Socket/SocketContext";
import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles((theme) => ({
  mainContainer: {
    flex: 1,
    padding: theme.spacing(2),
    background: "#f8fafc",
    minHeight: "calc(100vh - 100px)",
  },
  header: {
    background: "#fff",
    borderRadius: "8px",
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
  },
  headerContent: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  titleContainer: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(0.5),
  },
  headerTitle: {
    fontSize: "1.25rem",
    fontWeight: 600,
    color: "#1a237e",
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    "& .MuiSvgIcon-root": {
      color: "#1a237e",
      fontSize: "1.5rem",
    }
  },
  headerDescription: {
    color: "#64748b",
    fontSize: "0.813rem",
  },
  headerButton: {
    background: "#1a237e",
    color: "#fff",
    borderRadius: "6px",
    padding: "0 16px",
    height: "36px",
    fontSize: "0.813rem",
    fontWeight: 600,
    textTransform: "none",
    boxShadow: "0 1px 2px rgba(26,35,126,0.15)",
    "&:hover": {
      background: "#0d47a1",
      boxShadow: "0 2px 4px rgba(26,35,126,0.2)",
    }
  },
  queueGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(250px, 1fr))",
    gap: theme.spacing(2),
  },
  queueCard: {
    background: "#fff",
    borderRadius: "8px",
    overflow: "hidden",
    transition: "transform 0.2s ease, box-shadow 0.2s ease",
    border: "1px solid #e2e8f0",
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
    }
  },
  cardHeader: {
    padding: theme.spacing(1.5),
    borderBottom: "1px solid #f1f5f9",
    background: "linear-gradient(to right, #f8faff, #fff)",
  },
  queueInfo: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(0.25),
  },
  queueId: {
    fontSize: "0.688rem",
    color: "#64748b",
    fontWeight: 500,
  },
  queueName: {
    fontSize: "0.938rem",
    fontWeight: 600,
    color: "#1e293b",
    marginBottom: theme.spacing(0.5),
  },
  cardContent: {
    padding: theme.spacing(1.5),
  },
  colorContainer: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(1),
    background: "#f8fafc",
    borderRadius: "4px",
    marginBottom: theme.spacing(1.5),
  },
  colorLabel: {
    fontSize: "0.75rem",
    color: "#64748b",
    fontWeight: 500,
  },
  colorBox: {
    width: "20px",
    height: "20px",
    borderRadius: "4px",
    boxShadow: "inset 0 1px 2px rgba(0,0,0,0.06)",
  },
  greetingMessage: {
    padding: theme.spacing(1),
    background: "#f8fafc",
    borderRadius: "4px",
    border: "1px solid #e2e8f0",
    "& .messageLabel": {
      fontSize: "0.75rem",
      color: "#64748b",
      fontWeight: 500,
      marginBottom: theme.spacing(0.5),
      display: "block",
    },
    "& .messageText": {
      fontSize: "0.813rem",
      color: "#334155",
      lineHeight: "1.4",
    }
  },
  actionButtons: {
    display: "flex",
    gap: theme.spacing(1),
    marginTop: theme.spacing(1.5),
    padding: theme.spacing(1.5, 0, 0),
    borderTop: "1px solid #f1f5f9",
  },
  actionButton: {
    flex: 1,
    minWidth: "32px",
    height: "32px",
    borderRadius: "4px",
    padding: 0,
    "&:hover": {
      transform: "translateY(-1px)",
    }
  },
  editButton: {
    color: "#1e40af",
    background: "#dbeafe",
    "&:hover": {
      background: "#bfdbfe",
    }
  },
  deleteButton: {
    color: "#be123c",
    background: "#ffe4e6",
    "&:hover": {
      background: "#fecdd3",
    }
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: theme.spacing(4),
    gridColumn: "1 / -1",
  }
}));

const reducer = (state, action) => {
  if (action.type === "LOAD_QUEUES") {
    const queues = action.payload;
    const newQueues = [];

    queues.forEach((queue) => {
      const queueIndex = state.findIndex((q) => q.id === queue.id);
      if (queueIndex !== -1) {
        state[queueIndex] = queue;
      } else {
        newQueues.push(queue);
      }
    });

    return [...state, ...newQueues];
  }

  if (action.type === "UPDATE_QUEUES") {
    const queue = action.payload;
    const queueIndex = state.findIndex((u) => u.id === queue.id);

    if (queueIndex !== -1) {
      state[queueIndex] = queue;
      return [...state];
    } else {
      return [queue, ...state];
    }
  }

  if (action.type === "DELETE_QUEUE") {
    const queueId = action.payload;
    const queueIndex = state.findIndex((q) => q.id === queueId);
    if (queueIndex !== -1) {
      state.splice(queueIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }
};

const Queues = () => {
  const classes = useStyles();

  const [queues, dispatch] = useReducer(reducer, []);
  const [loading, setLoading] = useState(false);

  const [queueModalOpen, setQueueModalOpen] = useState(false);
  const [selectedQueue, setSelectedQueue] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const socketManager = useContext(SocketContext);
  const { user } = useContext(AuthContext);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/queue");
        dispatch({ type: "LOAD_QUEUES", payload: data });

        setLoading(false);
      } catch (err) {
        toastError(err);
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
		const socket = socketManager.getSocket(user.companyId);

		const onCompanyQueue = (data) => {
			if (data.action === "update" || data.action === "create") {
				dispatch({ type: "UPDATE_QUEUES", payload: data.queue });
			}

			if (data.action === "delete") {
				dispatch({ type: "DELETE_QUEUE", payload: data.queueId });
			}
		};
		socket.on(`company-${user.companyId}-queue`, onCompanyQueue);

		return () => {
		  socket.off(`company-${user.companyId}-queue`, onCompanyQueue);
		};
	}, [socketManager, user]);

  const handleOpenQueueModal = () => {
    setQueueModalOpen(true);
    setSelectedQueue(null);
  };

  const handleCloseQueueModal = () => {
    setQueueModalOpen(false);
    setSelectedQueue(null);
  };

  const handleEditQueue = (queue) => {
    setSelectedQueue(queue);
    setQueueModalOpen(true);
  };

  const handleCloseConfirmationModal = () => {
    setConfirmModalOpen(false);
    setSelectedQueue(null);
  };

  const handleDeleteQueue = async (queueId) => {
    try {
      await api.delete(`/queue/${queueId}`);
      toast.success(i18n.t("Queue deleted successfully!"));
    } catch (err) {
      toastError(err);
    }
    setSelectedQueue(null);
  };

  return (
    <MainContainer>
      <div className={classes.header}>
        <div className={classes.headerContent}>
          <div className={classes.titleContainer}>
            <Typography className={classes.headerTitle}>
              <QueueIcon />
              Administrador de Colas
            </Typography>
            <Typography className={classes.headerDescription}>
              Organiza y gestiona las colas de atención
            </Typography>
          </div>
          <Button
            variant="contained"
            className={classes.headerButton}
            onClick={handleOpenQueueModal}
          >
            Nova Fila
          </Button>
        </div>
      </div>

      <div className={classes.queueGrid}>
        {loading ? (
          <div className={classes.loadingContainer}>
            <CircularProgress size={40} thickness={4} sx={{ color: "#1a237e" }} />
          </div>
        ) : (
          queues.map((queue) => (
            <Card key={queue.id} className={classes.queueCard}>
              <div className={classes.cardHeader}>
                <div className={classes.queueInfo}>
                  <Typography className={classes.queueId}>
                    #{queue.id}
                  </Typography>
                  <Typography className={classes.queueName}>
                    {queue.name}
                  </Typography>
                </div>
              </div>

              <div className={classes.cardContent}>
                <div className={classes.colorContainer}>
                  <Typography className={classes.colorLabel}>
                    Cor da Fila
                  </Typography>
                  <div
                    className={classes.colorBox}
                    style={{ backgroundColor: queue.color }}
                  />
                </div>

                <div className={classes.greetingMessage}>
                  <span className="messageLabel">Mensagem de Saudação</span>
                  <Typography className="messageText">
                    {queue.greetingMessage}
                  </Typography>
                </div>

                <div className={classes.actionButtons}>
                  <Button
                    className={`${classes.actionButton} ${classes.editButton}`}
                    onClick={() => handleEditQueue(queue)}
                    title="Editar fila"
                  >
                    <Edit fontSize="small" />
                  </Button>
                  <Button
                    className={`${classes.actionButton} ${classes.deleteButton}`}
                    onClick={() => {
                      setSelectedQueue(queue);
                      setConfirmModalOpen(true);
                    }}
                    title="Excluir fila"
                  >
                    <DeleteOutline fontSize="small" />
                  </Button>
                </div>
              </div>
            </Card>
          ))
        )}
      </div>

      <QueueModal
        open={queueModalOpen}
        onClose={handleCloseQueueModal}
        queueId={selectedQueue?.id}
      />

      <ConfirmationModal
        title={selectedQueue && `Deletar Fila ${selectedQueue.name}?`}
        open={confirmModalOpen}
        onClose={handleCloseConfirmationModal}
        onConfirm={() => handleDeleteQueue(selectedQueue.id)}
      >
        Esta ação não pode ser desfeita.
      </ConfirmationModal>
    </MainContainer>
  );
};

export default Queues;
